import { useState, useCallback, useEffect } from 'react';
import {
  ProjectDto,
  CreateSubProjectRequest,
  projectsApi,
} from '../services/projects';

interface UseOpusDashboardOptions {
  opusId: string;
}

interface UseOpusDashboardReturn {
  opus: ProjectDto | null;
  pieces: ProjectDto[];
  isLoading: boolean;
  error: string | null;
  loadOpus: () => Promise<void>;
  loadPieces: () => Promise<void>;
  createPiece: (data: CreateSubProjectRequest) => Promise<ProjectDto>;
  updatePiece: (pieceId: string, data: { title: string; description?: string }) => Promise<ProjectDto>;
  completePiece: (pieceId: string) => Promise<ProjectDto>;
  archivePiece: (pieceId: string) => Promise<ProjectDto>;
  movePieceToStage: (pieceId: string, newStage: string) => Promise<ProjectDto>;
  isCreating: boolean;
  completedCount: number;
  totalCount: number;
  progressPercentage: number;
}

export const useOpusDashboard = ({
  opusId,
}: UseOpusDashboardOptions): UseOpusDashboardReturn => {
  const [opus, setOpus] = useState<ProjectDto | null>(null);
  const [pieces, setPieces] = useState<ProjectDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadOpus = useCallback(async () => {
    if (!opusId) return;

    try {
      const opusData = await projectsApi.getProject(opusId);
      setOpus(opusData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load opus');
    }
  }, [opusId]);

  const loadPieces = useCallback(async () => {
    if (!opusId) return;

    try {
      const piecesData = await projectsApi.getSubProjects(opusId);
      setPieces(piecesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pieces');
    }
  }, [opusId]);

  // Initial load
  useEffect(() => {
    const loadAll = async () => {
      if (!opusId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const [opusData, piecesData] = await Promise.all([
          projectsApi.getProject(opusId),
          projectsApi.getSubProjects(opusId),
        ]);
        setOpus(opusData);
        setPieces(piecesData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load opus data');
      } finally {
        setIsLoading(false);
      }
    };

    loadAll();
  }, [opusId]);

  const createPiece = useCallback(
    async (data: CreateSubProjectRequest): Promise<ProjectDto> => {
      setIsCreating(true);
      setError(null);
      try {
        const newPiece = await projectsApi.createSubProject(opusId, data);
        setPieces((prev) => [...prev, newPiece]);

        // Reload opus to update counts
        const updatedOpus = await projectsApi.getProject(opusId);
        setOpus(updatedOpus);

        return newPiece;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create piece';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setIsCreating(false);
      }
    },
    [opusId]
  );

  const updatePiece = useCallback(
    async (pieceId: string, data: { title: string; description?: string }): Promise<ProjectDto> => {
      try {
        const updatedPiece = await projectsApi.updateProject(pieceId, data);
        setPieces((prev) =>
          prev.map((p) => (p.id === pieceId ? updatedPiece : p))
        );
        return updatedPiece;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update piece';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    []
  );

  const completePiece = useCallback(
    async (pieceId: string): Promise<ProjectDto> => {
      try {
        const completedPiece = await projectsApi.completeProject(pieceId);
        setPieces((prev) =>
          prev.map((p) => (p.id === pieceId ? completedPiece : p))
        );

        // Reload opus to update counts
        const updatedOpus = await projectsApi.getProject(opusId);
        setOpus(updatedOpus);

        return completedPiece;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to complete piece';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [opusId]
  );

  const archivePiece = useCallback(
    async (pieceId: string): Promise<ProjectDto> => {
      try {
        const archivedPiece = await projectsApi.archiveProject(pieceId);
        setPieces((prev) => prev.filter((p) => p.id !== pieceId));

        // Reload opus to update counts
        const updatedOpus = await projectsApi.getProject(opusId);
        setOpus(updatedOpus);

        return archivedPiece;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to archive piece';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    [opusId]
  );

  const movePieceToStage = useCallback(
    async (pieceId: string, newStage: string): Promise<ProjectDto> => {
      try {
        const updatedPiece = await projectsApi.changeStage(pieceId, newStage);
        setPieces((prev) =>
          prev.map((p) => (p.id === pieceId ? updatedPiece : p))
        );
        return updatedPiece;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to move piece';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    },
    []
  );

  // Calculate counts
  const completedCount = pieces.filter((p) => p.status === 'Completed').length;
  const totalCount = pieces.length;
  const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return {
    opus,
    pieces,
    isLoading,
    error,
    loadOpus,
    loadPieces,
    createPiece,
    updatePiece,
    completePiece,
    archivePiece,
    movePieceToStage,
    isCreating,
    completedCount,
    totalCount,
    progressPercentage,
  };
};

export default useOpusDashboard;
